import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { DetailField, SectionCard, StatusPill } from "./shared";
import { ledgerGroupLabels } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";

export default function LedgerViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ledger, setLedger] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ledgerRes, balanceRes] = await Promise.all([
          api.get(`/society/accounting/engine/ledgers/${id}`),
          api.get(`/society/accounting/engine/ledgers/${id}/balance`).catch(() => ({ data: null })),
        ]);
        setLedger(ledgerRes.data);
        setBalance(balanceRes.data);
      } catch (err) {
        toast.error(formatErr(err, "Could not load this ledger"));
        navigate(`${ACCOUNTING_BASE}/ledgers`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!ledger) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <Link to={`${ACCOUNTING_BASE}/ledgers`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
            &larr; Ledgers
          </Link>
          <h2 className="mt-1 text-lg font-extrabold text-[#111827]">
            {ledger.name}
            {ledger.isSystem && <span className="ml-2 rounded-sm bg-[#F3F4F6] px-2 py-0.5 text-xs font-semibold uppercase text-[#6B7280]">System ledger</span>}
          </h2>
        </div>
        {!ledger.isSystem && (
          <Button onClick={() => navigate(`${ACCOUNTING_BASE}/ledgers/${id}/edit`)} className="bg-[#111827] text-white hover:bg-[#374151]">
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </Button>
        )}
      </div>

      <SectionCard title="Ledger details">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailField label="Ledger name" value={ledger.name} />
          <DetailField label="Code" value={ledger.code} />
          <DetailField label="Type" value={ledgerGroupLabels[ledger.ledgerGroup] || ledger.ledgerGroup} />
          <DetailField label="Linked entity" value={ledger.linkedEntity} />
          <DetailField label="Derived balance" value={balance ? `₹ ${Number(balance.balance || 0).toLocaleString()} (${balance.side})` : "-"} />
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Status</div>
            <div className="mt-1"><StatusPill active={ledger.active !== false} /></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Ledger history</div>
            <div className="mt-1"><StatusPill active={!ledger.hasPostedTransactions} trueLabel="No posted vouchers yet" falseLabel="Has posted vouchers" /></div>
          </div>
        </div>
        {ledger.notes && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Notes</div>
            <p className="mt-1 text-sm text-[#374151]">{ledger.notes}</p>
          </div>
        )}
      </SectionCard>

      {!ledger.isSystem && (
        <SectionCard title="Record history">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <DetailField label="Created" value={ledger.createdAt ? new Date(ledger.createdAt).toLocaleString() : "-"} />
            <DetailField label="Last updated" value={ledger.updatedAt ? new Date(ledger.updatedAt).toLocaleString() : "-"} />
          </div>
        </SectionCard>
      )}
    </div>
  );
}
